/**
 * Pre-flight environment checks.
 *
 * Web Serial fails in three distinct ways that all look like "the button does
 * nothing" to a user: the browser has no implementation, the page is not a
 * secure context, or an embedding frame withheld the permission. We separate
 * them so the UI can say which one it is instead of a generic "unsupported".
 */

export type EnvironmentStatus = "ready" | "unsupported-browser" | "insecure-context" | "blocked-in-frame";

export interface EnvironmentReport {
  status: EnvironmentStatus;
  supported: boolean;
  browserName: string;
  /** Short sentence naming the problem. */
  headline: string;
  /** What the user should do about it. */
  guidance: string[];
}

interface BrowserGuess {
  name: string;
  likelySupported: boolean;
}

function guessBrowser(userAgent: string): BrowserGuess {
  const ua = userAgent.toLowerCase();

  // Order matters: most Chromium forks also claim "chrome".
  if (ua.includes("firefox")) return { name: "Firefox", likelySupported: false };
  if (ua.includes("edg/")) return { name: "Microsoft Edge", likelySupported: true };
  if (ua.includes("opr/") || ua.includes("opera")) return { name: "Opera", likelySupported: true };
  if (ua.includes("brave")) return { name: "Brave", likelySupported: true };
  if (ua.includes("chrome") || ua.includes("chromium")) return { name: "Chrome", likelySupported: true };
  if (ua.includes("safari")) return { name: "Safari", likelySupported: false };
  return { name: "this browser", likelySupported: false };
}

function isMobile(userAgent: string): boolean {
  return /android|iphone|ipad|ipod|mobile/i.test(userAgent);
}

export function checkEnvironment(): EnvironmentReport {
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const browser = guessBrowser(userAgent);
  const hasSerial = typeof navigator !== "undefined" && "serial" in navigator;

  if (hasSerial) {
    return {
      status: "ready",
      supported: true,
      browserName: browser.name,
      headline: `Web Serial is available in ${browser.name}.`,
      guidance: [],
    };
  }

  // navigator.serial is only exposed in secure contexts, so an insecure origin
  // is indistinguishable from an unsupported browser unless we check directly.
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return {
      status: "insecure-context",
      supported: false,
      browserName: browser.name,
      headline: "This page is not running in a secure context.",
      guidance: [
        "Web Serial is only exposed over HTTPS or on localhost.",
        `Open the site via https:// instead of http:// (current origin: ${window.location.origin}).`,
        "If you are testing on another device over your LAN, use a tunnel such as `ngrok http 5173` or `cloudflared tunnel` to get an HTTPS URL.",
      ],
    };
  }

  // A cross-origin iframe without allow="serial" also hides the API.
  if (typeof window !== "undefined" && window.self !== window.top && browser.likelySupported) {
    return {
      status: "blocked-in-frame",
      supported: false,
      browserName: browser.name,
      headline: "Web Serial is blocked inside this embedded frame.",
      guidance: [
        'The page embedding this flasher must set allow="serial" on the iframe.',
        "Opening the flasher in its own tab will also work.",
      ],
    };
  }

  const mobile = isMobile(userAgent);

  return {
    status: "unsupported-browser",
    supported: false,
    browserName: browser.name,
    headline: `${browser.name} does not implement the Web Serial API.`,
    guidance: mobile
      ? [
          "Web Serial is not available on iOS at all, and on Android only in Chrome with a USB-OTG adapter.",
          "Flash from a desktop or laptop instead.",
        ]
      : [
          "Use Chrome, Edge, Opera, or Brave (version 89 or newer).",
          "Firefox and Safari have declined to implement Web Serial, so they cannot be supported.",
          "If you are already in Chrome, check that chrome://flags is not disabling the Serial API.",
        ],
  };
}
